import { IoMdArrowBack } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IoClose, IoSearchOutline } from "react-icons/io5";
import SkeletonOne from "../../components/skeleton/SkeletonOne";
import { groupChatFriends, createGroupChat } from "../../redux/ApiSlice/MessageGroupSlice";
import { recommendedFriends, getAndCreateChatRoom } from "../../redux/ApiSlice/MessagesSlice";

const MessageCreateNew = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState("");
    const [showGroupPopup, setShowGroupPopup] = useState(false);
    const [groupSearch, setGroupSearch] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);
    // New states for group details step
    const [showGroupDetails, setShowGroupDetails] = useState(false);
    const [groupName, setGroupName] = useState("");
    const [groupImage, setGroupImage] = useState("");
    const [imagePreview, setImagePreview] = useState("");
    const [imageFile, setImageFile] = useState(null);
    const [loadingUserId, setLoadingUserId] = useState(null);

    const { groupChatFriendsList, createGroupLoading } = useSelector((state) => state.messageGroup);
    const { recommendedFriendsList = [], loading: friendsLoading } = useSelector((state) => state.messages || {});

    // Filter users based on search term (from Redux recommendedFriendsList)
    const filteredUsers = (recommendedFriendsList || []).filter(user =>
        (user.full_name && user.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.username && user.username.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    // Filter real users in popup (from API)
    const filteredGroupFriends = groupChatFriendsList.filter(user =>
        user.full_name.toLowerCase().includes(groupSearch.toLowerCase()) ||
        user.username.toLowerCase().includes(groupSearch.toLowerCase())
    );

    // Handle search input change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Handle group popup search
    const handleGroupSearch = (e) => {
        setGroupSearch(e.target.value);
    };

    // Toggle user selection in popup
    const handleSelectUser = (id) => {
        setSelectedUsers(prev =>
            prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]
        );
    };

    // Handle add to group (step 1: go to group details step)
    const handleAddToGroup = () => {
        if (selectedUsers.length > 0) {
            setShowGroupDetails(true);
        }
    };

    // Handle group creation (step 2: submit group details)
    const handleCreateGroup = async () => {
        if (!groupName.trim()) return;
        const formData = new FormData();
        formData.append("name", groupName);
        if (imageFile) {
            formData.append("image", imageFile);
        }
        selectedUsers.forEach(id => {
            formData.append("user_ids", id);
        });

        await dispatch(createGroupChat(formData));
        navigate("/message", { state: { activeTab: "Groups" } });

        setShowGroupDetails(false);
        setShowGroupPopup(false);
        setSelectedUsers([]);
        setGroupSearch("");
        setGroupName("");
        setImageFile(null);
        setImagePreview("");
    };

    // Handle image file selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle start chat with a user
    const handleStartChat = async (userId) => {
        try {
            setLoadingUserId(userId);
            const response = await dispatch(getAndCreateChatRoom(userId));
            if (response.payload.id) {
                navigate(`/message/${response.payload.id}`);
            }
        } catch (error) {
  console.error(error);
} finally {
            setLoadingUserId(null);
        }
    };

    useEffect(() => {
        dispatch(groupChatFriends());
        dispatch(recommendedFriends());
    }, [dispatch]);

    return (
        <div className="h-full bg-white dark:bg-[#1E1D21]">
            <div className="sticky top-0 z-10">
                <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                    <IoMdArrowBack
                        onClick={() => navigate(-1)}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                    />
                    <p className="font-bold text-[18px] text-black dark:text-black">New Messages</p>
                </div>
                <div className="flex items-center gap-x-2 px-2 py-1.5">
                    <div className="w-full flex justify-start relative">
                        <IoSearchOutline className="absolute top-2 right-2 cursor-pointer text-black dark:text-white" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={handleSearchChange}
                            placeholder="Search for people"
                            className="rounded-full text-[14px] w-full py-1.5 pr-8 pl-3 bg-gray-100 dark:bg-[#333333] text-black dark:text-white border border-gray-900 outline-none"
                        />
                    </div>
                    {/* <button
                        className="bg-black dark:bg-gray-900 text-white dark:text-white rounded-md px-3 py-1.5 text-[12px] whitespace-nowrap"
                        onClick={() => setShowGroupPopup(true)}
                    >
                        New Group Chats
                    </button> */}
                </div>
            </div>

            <div className="overflow-y-scroll no-scrollbar mb-2 h-[-webkit-fill-available] absolute w-full">
                {filteredUsers.length > 0 &&
                    <p className="text-[16px] font-bold text-black dark:text-white pt-2 px-2 mb-1.5">Recommended</p>
                }
                {filteredUsers.map((data) => (
                    <div className="flex items-center justify-between gap-x-1 border-b border-gray-300 dark:border-gray-800/50 last:border-none px-2 py-1.5" key={data.id}>
                        <div className="flex items-center gap-x-2">
                            <img
                                alt={data.full_name}
                                src={data.avatar_url}
                                onClick={() => navigate(`/account/${data.id}`)}
                                className="w-9 h-9 rounded-full object-cover cursor-pointer bg-gray-200 border border-gray-300 dark:border-gray-700"
                            />
                            <div className="flex-col">
                                <p
                                    onClick={() => navigate(`/account/${data.id}`)}
                                    className="text-[12px] font-semibold text-black dark:text-white cursor-pointer line-clamp-1"
                                >
                                    {data.full_name}
                                </p>
                                <p
                                    onClick={() => navigate(`/account/${data.id}`)}
                                    className="text-[12px] font-medium text-gray-500 dark:text-gray-400 cursor-pointer line-clamp-1"
                                >
                                    @{data.username}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-2">
                            <button
                                onClick={() => handleStartChat(data.id)}
                                disabled={loadingUserId === data.id}
                                className={`text-[12px] px-2 py-1 rounded-md font-semibold text-black dark:text-white cursor-pointer disabled:cursor-not-allowed min-w-[70px] flex items-center justify-center ${loadingUserId === data.id
                                    ? 'bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse'
                                    : 'bg-[#BDE6FF] dark:bg-[#333333]'
                                    }`}
                            >
                                Message
                            </button>
                            <button className="bg-gray-200 dark:bg-[#333333] text-gray-900 dark:text-gray-100 text-[10px] p-1 rounded-lg">
                                <IoClose className="text-[16px]" />
                            </button>
                        </div>
                    </div>
                ))}
                {filteredUsers.length === 0 && !friendsLoading && (
                    <p className="text-[12px] text-center mt-20 text-gray-600 dark:text-gray-700">No users found</p>
                )}
                {friendsLoading && !loadingUserId && (
                    <SkeletonOne />
                )}
            </div>

            {/* Popup for New Group Chats */}
            {showGroupPopup && !showGroupDetails && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="m-2 bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-md p-3 relative">
                        <div className="flex items-center gap-x-2 mb-2">
                            <input
                                type="text"
                                value={groupSearch}
                                onChange={handleGroupSearch}
                                placeholder="Search users"
                                className="rounded-full w-full p-1 pl-4 pr-2 outline-none text-black dark:text-white bg-gray-100 dark:bg-gray-800 placeholder:text-[14px]"
                            />
                            <div className="flex items-center justify-center w-7 h-7 bg-gray-200 dark:bg-gray-800 rounded-md cursor-pointer">
                                <IoClose
                                    onClick={() => { setShowGroupPopup(false); setSelectedUsers([]); }}
                                    className="text-[20px] text-gray-500 hover:text-black dark:hover:text-white"
                                />
                            </div>
                        </div>
                        <div className="max-h-60 overflow-y-auto mb-3">
                            {filteredGroupFriends.map(user => (
                                <div
                                    key={user.id}
                                    onClick={() => handleSelectUser(user.id)}
                                    className="flex items-center gap-x-2 p-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 border-b border-gray-300 dark:border-gray-800"
                                >
                                    <img
                                        alt={user.full_name}
                                        src={user.avatar_url}
                                        className={`w-10 h-10 rounded-full object-cover bg-gray-200 border-2 transition-all duration-150 ${selectedUsers.includes(user.id) ? 'border-blue-500 shadow-md' : 'border-transparent'}`}
                                    />
                                    <div className="flex flex-col">
                                        <p className="text-[15px] text-black dark:text-white line-clamp-1">{user.full_name}</p>
                                        <p className="text-[12px] text-gray-400">{user.username}</p>
                                    </div>
                                </div>
                            ))}
                            {filteredGroupFriends.length === 0 && (
                                <p className="text-center text-[12px] text-gray-500 py-4">No users found</p>
                            )}
                        </div>
                        <button
                            disabled={selectedUsers.length === 0}
                            onClick={handleAddToGroup}
                            className={`w-full py-2 rounded-md text-white font-semibold transition-all duration-150 ${selectedUsers.length > 0 ? 'bg-blue-700 hover:bg-blue-800' : 'bg-gray-400 dark:bg-gray-800 cursor-not-allowed'}`}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* Step 2: Group Details Modal */}
            {showGroupDetails && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="m-2 bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-md p-3 relative">
                        {/* <button
                            className="absolute top-0 right-1 text-gray-500 hover:text-black dark:hover:text-white text-xl"
                            onClick={() => { setShowGroupDetails(false); }}
                        >
                            &times;
                        </button> */}
                        <div className="flex items-center justify-between gap-x-2 mb-4">
                            <h2 className="text-lg font-bold text-black dark:text-white">Group Details</h2>
                            <div className="flex items-center justify-center w-7 h-7 bg-gray-200 dark:bg-gray-800 rounded-md cursor-pointer">
                                <IoClose
                                    onClick={() => { setShowGroupDetails(false); }}
                                    className="text-[20px] text-gray-500 hover:text-black dark:hover:text-white"
                                />
                            </div>
                        </div>
                        <input
                            type="text"
                            value={groupName}
                            onChange={e => setGroupName(e.target.value)}
                            placeholder="Enter group name"
                            className="rounded-md w-full p-2 mb-2 outline-none text-black dark:text-white bg-gray-100 dark:bg-gray-800 placeholder:text-[14px]"
                        />
                        <div className="mb-2 flex flex-col items-center">
                            {imagePreview && (
                                <img src={imagePreview} alt="Group" className="w-20 h-20 rounded-full object-cover mb-2" />
                            )}
                            <label className="cursor-pointer text-white bg-black dark:bg-gray-600 px-4 py-2 rounded-md font-semibold hover:bg-gray-900 dark:hover:bg-gray-700 transition-all duration-150">
                                {imagePreview ? 'Change Image' : 'Upload Group Image'}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                            </label>
                            {imageFile && (
                                <span className="text-xs text-gray-500 mt-1 max-w-[180px] truncate">Image selected</span>
                            )}
                        </div>
                        <button
                            disabled={!groupName.trim() || createGroupLoading}
                            onClick={handleCreateGroup}
                            className={`w-full py-2 rounded-md text-white font-semibold transition-all duration-150 
                                ${groupName.trim() && !createGroupLoading ? 'bg-blue-700 hover:bg-blue-800' : 'bg-gray-400 dark:bg-gray-800 cursor-not-allowed'}`
                            }
                        >
                            {createGroupLoading ? 'Creating ...' : 'Create Group'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default MessageCreateNew